import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Box, Paper, TableContainer, LinearProgress, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from "react-router";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useRecordings } from './useRecordings';
import ErrorNotification from '../../../components/ErrorNotification/ErrorNotification';
import api from "../../../services/api";
import { useNotifier } from "muinotify";
import useSWR from 'swr';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getExpectedEpisodesPerRecording,
  hasRecordingEpisodeCountMismatch,
  formatRecordingEpisodeCountMessage,
} from '../../../components/utils/recordingBudgetUtils';

const Recordings = () => {
  const { notifier } = useNotifier();
  const [selectedProductionId, setSelectedProductionId] = useState(null);
  const { recordings, isLoading, error, mutate } = useRecordings(selectedProductionId);
  const { data: productions } = useSWR('/productions');
  const [isUpdating, setIsUpdating] = useState(false);
  const NAV_URL = `recordings`;
  const URL = `recordings`;

  useEffect(() => {
    // Load selected production from localStorage if available
    const savedProduction = localStorage.getItem("selectedProduction");
    if (savedProduction && productions) {
      try {
        const parsedProduction = JSON.parse(savedProduction);
        const production = productions.find((p) => p.id === parsedProduction.id);
        if (production) {
          setSelectedProductionId(production.id);
        }
      } catch (e) {
        console.error("Error parsing saved production:", e);
      }
    }
  }, [productions]);

  const handleProductionChange = (event) => {
    const productionId = event.target.value;
    setSelectedProductionId(productionId);
    // Save to localStorage
    if (productions) {
      const production = productions.find((p) => p.id === productionId);
      if (production) {
        localStorage.setItem("selectedProduction", JSON.stringify(production));
      }
    }
  };

  function getRowId(row) {
    return row.id;
  }

  let navigate = useNavigate();

  const handleEdit = (id) => {
    navigate(`/${NAV_URL}/update/${id}`);
  };

  const handleAdd = () => {
    if (!selectedProductionId) {
      notifier("Παρακαλώ επιλέξτε πρώτα μια παραγωγή.", { variant: "warning" });
      return;
    }
    navigate(`/${NAV_URL}/create`);
  };

  const handleDelete = useCallback((id) => {
    confirmAlert({
      title: 'Επιβεβαίωση διαγραφής',
      message: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την εγγραφή;',
      buttons: [
        {
          label: 'Ναι',
          onClick: async () => {
            try {
              setIsUpdating(true);
              await api.delete(`/${URL}/${id}`);
              notifier("Εγγραφή διαγράφηκε επιτυχώς.", { variant: "success" });
              await mutate();
            } catch (err) {
              notifier("Αποτυχία διαγραφής εγγραφής. " + err.message, { variant: "error" });
            } finally {
              setIsUpdating(false);
            }
          }
        },
        {
          label: 'Όχι',
        }
      ]
    });
  }, [mutate, notifier, URL]);

  const selectedProduction = productions?.find((p) => p.id === selectedProductionId);
  const expectedEpisodesPerRecording = getExpectedEpisodesPerRecording(
    selectedProduction?.no_of_episodes,
    recordings?.length,
    selectedProduction?.episodes_per_recording
  );

  const columns = [
    { 
      field: 'rec_number', 
      headerName: 'Αριθμός Εγγραφής', 
      flex: 1, 
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'rec_date',
      headerName: 'Ημερομηνία',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleDateString('el-GR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'episodes',
      headerName: 'Επεισόδια',
      flex: 2,
      headerAlign: 'center',
      renderCell: (params) => {
        const episodes = params.value || [];
        if (episodes.length === 0) return '-';

        const episodeCountMismatch = hasRecordingEpisodeCountMismatch(
          episodes.length,
          expectedEpisodesPerRecording
        );
        const mismatchInfo = episodeCountMismatch
          ? {
              episodeCount: episodes.length,
              expectedEpisodeCount: expectedEpisodesPerRecording,
            }
          : null;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            {episodes.sort((a, b) => a - b).map((ep) => (
              <Chip key={ep} label={`E-${ep}`} size="small" />
            ))}
            {mismatchInfo && (
              <Tooltip title={formatRecordingEpisodeCountMessage(mismatchInfo)}>
                <Chip
                  icon={<WarningAmberIcon />}
                  label={`${episodes.length} επ. (αναμ. ~${expectedEpisodesPerRecording})`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'rec_notes',
      headerName: 'Σημειώσεις',
      flex: 2,
      headerAlign: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: "Ενέργειες",
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem 
          color="primary" 
          icon={<EditOutlinedIcon />} 
          label="Επεξεργασία" 
          onClick={() => handleEdit(params.row.id)} 
        />,
        <GridActionsCellItem 
          color="secondary" 
          icon={<DeleteOutlineOutlinedIcon />} 
          label="Διαγραφή" 
          onClick={() => handleDelete(params.row.id)} 
        />,
      ],
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Εγγραφές" module="Productions" subtitle="Λίστα εγγραφών" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormListToolbar
            handleAdd={handleAdd}
          />
          <Box m="8px 0 0 0" height="80vh">
            <Paper elevation={2} style={{ padding: '1rem', height: '100%', width: '100%' }}>
              {/* Production Selection */}
              <Box mb={2}>
                <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                  <InputLabel id="production-select-label">Παραγωγή</InputLabel>
                  <Select
                    labelId="production-select-label"
                    id="production-select"
                    value={selectedProductionId || ''}
                    label="Παραγωγή"
                    onChange={handleProductionChange}
                  >
                    {productions?.map((production) => (
                      <MenuItem key={production.id} value={production.id}>
                        {production.p_descr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {!selectedProductionId && (
                <Box p={4} textAlign="center">
                  <Typography variant="h6" color="text.secondary">
                    Παρακαλώ επιλέξτε μια παραγωγή για να δείτε τις εγγραφές
                  </Typography>
                </Box>
              )}

              {selectedProductionId && (
                <>
                  {isLoading && <LinearProgress />}
                  {error && <ErrorNotification ml={2} mr={2} error={error} />}
                  <TableContainer style={{ height: '87%', width: '100%' }}>
                    <DataGrid
                      rows={recordings || []}
                      columns={columns}
                      getRowId={getRowId}
                      loading={isLoading || isUpdating}
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderRight: '1px solid #ddd',
                          borderBottom: 'none',
                        },
                        '& .MuiDataGrid-columnHeader': {
                          borderRight: '1px solid #ddd',
                        },
                        '& .MuiDataGrid-root': {
                          border: 'none',
                        },
                        '& .MuiDataGrid-columnsContainer': {
                          backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          borderTop: '1px solid #e0e0e0',
                        },
                        '& .MuiTablePagination-root': {
                          color: '#000',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-row:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                      }}
                    />
                  </TableContainer>
                </>
              )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Recordings;

